/**
 * Draggable Stops List Component
 * List of stops with drag-and-drop reordering
 */

import { useState, useCallback } from 'react';
import { Badge, getStatusBadgeVariant } from '../ui';
import { GripVertical, MapPin, Clock, Package, RotateCcw, Trash2, Banknote } from 'lucide-react';
import { cn } from '../../utils/cn';
import type { RouteStop } from '../../types';
import { buildMerchantGroupColorMap, getMerchantBandColor } from '../../utils/merchantGroups';

interface DraggableStopsListProps {
  stops: RouteStop[];
  onReorder: (newOrder: string[]) => void;
  onRemoveStop?: (taskUid: string) => void;
  onStopClick?: (stop: RouteStop) => void;
  selectedStopId?: string;
  isReordering?: boolean;
}

interface DragState {
  draggedIndex: number | null;
  dragOverIndex: number | null;
}

export default function DraggableStopsList({
  stops,
  onReorder,
  onRemoveStop,
  onStopClick,
  selectedStopId,
  isReordering,
}: DraggableStopsListProps) {
  const [dragState, setDragState] = useState<DragState>({
    draggedIndex: null,
    dragOverIndex: null,
  });

  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    setDragState({ draggedIndex: index, dragOverIndex: null });
    
    // Add drag image
    const target = e.target as HTMLElement;
    e.dataTransfer.setDragImage(target, 20, 20);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (dragState.draggedIndex !== null && dragState.draggedIndex !== index) {
      setDragState(prev => ({ ...prev, dragOverIndex: index }));
    }
  }, [dragState.draggedIndex]);

  const handleDragLeave = useCallback(() => {
    setDragState(prev => ({ ...prev, dragOverIndex: null }));
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    const dragIndex = dragState.draggedIndex;
    if (dragIndex === null || dragIndex === dropIndex) {
      setDragState({ draggedIndex: null, dragOverIndex: null });
      return;
    }

    // Reorder stops
    const newStops = [...stops];
    const [draggedStop] = newStops.splice(dragIndex, 1);
    newStops.splice(dropIndex, 0, draggedStop);

    // Get new order as task_uids
    const newOrder = newStops.map(s => s.task_uid);
    onReorder(newOrder);

    setDragState({ draggedIndex: null, dragOverIndex: null });
  }, [dragState.draggedIndex, stops, onReorder]);

  const handleDragEnd = useCallback(() => {
    setDragState({ draggedIndex: null, dragOverIndex: null });
  }, []);

  const getStopIcon = (type: RouteStop['type']) => {
    switch (type) {
      case 'pickup':
        return <Package className="h-4 w-4 text-green-600" />;
      case 'rto':
        return <RotateCcw className="h-4 w-4 text-yellow-600" />;
      case 'cash':
        return <Banknote className="h-4 w-4 text-amber-600" />;
      case 'depot':
        return <MapPin className="h-4 w-4 text-blue-600" />;
      default:
        return <MapPin className="h-4 w-4 text-gray-600" />;
    }
  };

  const formatTime = (dateStr?: string) => {
    if (!dateStr) return '--:--';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'Asia/Baghdad',
    });
  };

  if (stops.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <MapPin className="h-8 w-8 mx-auto mb-2 text-gray-300" />
        <p>No stops in this route</p>
        <p className="text-sm">Drag tasks here to add them</p>
      </div>
    );
  }

  const merchantColors = buildMerchantGroupColorMap(stops);

  return (
    <div className={cn('space-y-1', isReordering && 'opacity-50 pointer-events-none')}>
      {stops.map((stop, index) => {
        const isDragging = dragState.draggedIndex === index;
        const isDragOver = dragState.dragOverIndex === index;
        const isSelected = selectedStopId === stop._id;
        const bandColor = getMerchantBandColor(stop, merchantColors);

        return (
          <div
            key={stop._id}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
            onClick={() => onStopClick?.(stop)}
            className={cn(
              'flex items-center gap-2 p-3 rounded-lg border transition-all cursor-pointer',
              'hover:shadow-sm',
              isDragging && 'opacity-50 scale-95',
              isDragOver && 'border-blue-500 bg-blue-50 border-dashed',
              isSelected
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300 bg-white'
            )}
            style={{
              borderLeft: bandColor ? `4px solid ${bandColor}` : undefined,
              backgroundColor: bandColor && !isSelected && !isDragOver ? `${bandColor}15` : undefined,
            }}
          >
            {/* Drag Handle */}
            <div className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600">
              <GripVertical className="h-5 w-5" />
            </div>

            {/* Sequence Number */}
            <div
              className={cn(
                'flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-medium',
                stop.status === 'completed'
                  ? 'bg-green-100 text-green-700'
                  : stop.status === 'failed'
                  ? 'bg-red-100 text-red-700'
                  : 'bg-gray-100 text-gray-700'
              )}
            >
              {index + 1}
            </div>

            {/* Stop Icon */}
            <div className="flex-shrink-0">{getStopIcon(stop.type)}</div>

            {/* Stop Details */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm text-gray-900 truncate">
                  {stop.merchant_name || stop.task_uid}
                </span>
                <Badge variant={getStatusBadgeVariant(stop.status)} size="sm">
                  {stop.status}
                </Badge>
              </div>
              <p className="text-xs text-gray-500 truncate mt-0.5">
                {stop.address || `${stop.lat.toFixed(4)}, ${stop.lng.toFixed(4)}`}
              </p>
            </div>

            {/* ETA & Actions */}
            <div className="flex items-center gap-2">
              <div className="text-right">
                <div className="flex items-center gap-1 text-sm text-gray-600">
                  <Clock className="h-3.5 w-3.5 text-gray-400" />
                  <span>{formatTime(stop.eta || stop.google_eta)}</span>
                </div>
                {stop.parcel_count && (
                  <p className="text-xs text-gray-500">{stop.parcel_count} parcels</p>
                )}
              </div>

              {/* Remove Button */}
              {onRemoveStop && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveStop(stop.task_uid);
                  }}
                  className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        );
      })}

      {/* Drop zone at the end */}
      {dragState.draggedIndex !== null && (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragState(prev => ({ ...prev, dragOverIndex: stops.length }));
          }}
          onDrop={(e) => handleDrop(e, stops.length)}
          className={cn(
            'h-12 border-2 border-dashed rounded-lg flex items-center justify-center text-sm text-gray-500',
            dragState.dragOverIndex === stops.length
              ? 'border-blue-500 bg-blue-50 text-blue-600'
              : 'border-gray-300'
          )}
        >
          Drop here to add at end
        </div>
      )}
    </div>
  );
}
